// ReplyCard.tsx — Presentational card for the profile "Replies" tab.
// Shows the user's reply, who they replied to, a quoted parent-post preview, and stats.
// The whole card links to the parent post when parentPost is non-null.
//
// DUMB component — calls NO data hooks. All resolved data is passed via props.

import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { Comment, Post, User } from '@/core/models';
import { Avatar } from '@/ui';
import { Icon } from '@/icons';
import { bodyToPlainText } from '@/components/PostCard/bodyText';

// ─── Props ────────────────────────────────────────────────────────────────────

// `author` is the reply's author (the profile being viewed).
// Comment.authorId is enough for the data layer, but the card needs a
// display name and handle — Comment carries no denormalised author fields,
// so the caller resolves and passes it in.
export interface ReplyCardProps {
  comment: Comment;
  author: User;
  parentPost: Post | null;
  parentAuthor: User | null;
  replyCount: number;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

// The wrapper is either a link (<Link>) or a plain <article> depending on
// whether parentPost is reachable. We share the visual chrome via a styled
// base and extend it for both variants.
const cardBase = css`
  display: block;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  text-decoration: none;
  color: inherit;
`;

const CardLink = styled(Link)`
  ${cardBase}
  /* Opaque when glass is OFF so the animated backdrop can't bleed through the card. */
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  transition: background 0.15s;



  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const CardArticle = styled.article`
  ${cardBase}
  /* Opaque when glass is OFF so the animated backdrop can't bleed through the card. */
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const AuthorName = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
`;

const HandleTime = styled.p`
  display: flex;
  column-gap: 0.9em;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ReplyingTo = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0 0 6px;
`;

const ReplyingHandle = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
`;

const BodyText = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  margin: 0 0 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const QuoteBlock = styled.div`
  border-left: 2px solid ${({ theme }) => theme.color.glassBorder};
  padding: 6px 10px;
  margin-bottom: 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: color-mix(in srgb, ${({ theme }) => theme.color.glassBorder} 40%, transparent);
`;

const QuoteHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
`;

const QuoteName = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const QuoteHandle = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const QuoteBody = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const QUOTE_MAX = 120;

function trimQuote(text: string): string {
  if (text.length <= QUOTE_MAX) return text;
  return text.slice(0, QUOTE_MAX).trimEnd() + '…';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ReplyCard({
  comment,
  author,
  parentPost,
  parentAuthor,
  replyCount,
}: ReplyCardProps) {
  // Prefer comment.replyingToHandle (denormalised attribution) — matches CommentCard
  // behaviour for depth-flattened nested replies.
  const replyingToHandle =
    comment.replyingToHandle ??
    parentAuthor?.handle ??
    parentPost?.handle ??
    'someone';

  const content = (
    <>
      {/* Header: reply author.
          Avatar and name are intentionally NOT links — the whole card is a single
          <Link> (or <article>), so nesting interactive elements would be invalid HTML. */}
      <HeaderRow>
        <Avatar name={author.name} size={34} />
        <MetaCol>
          <NameRow>
            <AuthorName>{author.name}</AuthorName>
          </NameRow>
          <HandleTime data-testid="reply-handle-time">
            {author.handle && <span>{author.handle}</span>}
            <span>{comment.time}</span>
          </HandleTime>
        </MetaCol>
      </HeaderRow>

      {/* "Replying to @handle" */}
      <ReplyingTo>
        Replying to{' '}
        <ReplyingHandle>{replyingToHandle}</ReplyingHandle>
      </ReplyingTo>

      {/* Reply body — rendered as plain text; body may contain legacy markup */}
      <BodyText>{bodyToPlainText(comment.body)}</BodyText>

      {/* Quoted parent preview — only when parentPost is available */}
      {parentPost !== null && (
        <QuoteBlock>
          <QuoteHead>
            <QuoteName>
              {parentAuthor?.name ?? parentPost.name}
            </QuoteName>
            <QuoteHandle>
              {parentAuthor?.handle ?? parentPost.handle}
            </QuoteHandle>
          </QuoteHead>
          <QuoteBody>
            {trimQuote(bodyToPlainText(parentPost.body))}
          </QuoteBody>
        </QuoteBlock>
      )}

      {/* Stats row */}
      <StatsRow>
        <Stat>
          <Icon name="cherish" size={14} />
          {comment.likes}
        </Stat>
        <Stat>
          <Icon name="reply" size={14} />
          {replyCount}
        </Stat>
      </StatsRow>
    </>
  );

  // Navigate to the parent post only when it is reachable.
  if (parentPost !== null) {
    return (
      <CardLink to={`/post/${comment.postId}`} aria-label="View post">
        {content}
      </CardLink>
    );
  }

  return <CardArticle>{content}</CardArticle>;
}
